import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { RestRequestService } from '@app/restApi/rest-request.service';
import {
  PrimeInvitation as Invitation,
  InvitationReceived,
  PrimeInviteGCPayload,
  PrimeInviteManagerPayload,
  UserInvited,
} from '@app/store/invitations/invitations.types';
import {
  REST_PRIME_INVITATIONS,
  REST_PRIME_INVITATIONS_RECEIVED,
  REST_INVITATIONS_SENT,
  REST_SUBCONTRACT_INVITATIONS,
  REST_SUBCONTRACT_INVITATIONS_RECEIVED,
  REST_BASE,
} from '@app/restApi/RestRoutes';
import { ProjectDto } from '@app/pages/webapp/projects/projects.interface';

@Injectable({
  providedIn: 'root',
})
export class InvitesApiService {
  restService = inject(RestRequestService);

  // loads both prime contract and subcontract invitations
  getInvitationsSent(): Observable<Invitation[]> {
    return this.restService.getWithObservable(REST_INVITATIONS_SENT);
  }

  getPrimeInvitationsReceived(): Observable<InvitationReceived[]> {
    return this.restService.getWithObservable(REST_PRIME_INVITATIONS_RECEIVED);
  }
  sendPrimeInvite(payload: PrimeInviteGCPayload | PrimeInviteManagerPayload) {
    return this.restService.postWithObservable(REST_PRIME_INVITATIONS, payload);
  }
  acceptPrimeInvite(inviteId: string, newProject: Partial<ProjectDto>) {
    return this.restService.postWithObservable(
      `${REST_PRIME_INVITATIONS}/${inviteId}/accept`,
      newProject,
    );
  }
  dismissPrimeInvite(inviteId: string) {
    return this.restService.postWithObservable(`${REST_PRIME_INVITATIONS}/${inviteId}/decline`, {});
  }
  resendPrimeInvite(inviteId: string) {
    return this.restService.postWithObservable(`${REST_PRIME_INVITATIONS}/${inviteId}/resend`, {});
  }
  cancelSentPrimeInvite(inviteId: string) {
    return this.restService.postWithObservable(`${REST_PRIME_INVITATIONS}/${inviteId}/cancel`, {});
  }

  getSubcontractInvitesReceived(): Observable<InvitationReceived[]> {
    return this.restService.getWithObservable(REST_SUBCONTRACT_INVITATIONS_RECEIVED);
  }
  sendSubcontractInvite(payload: { project_id: number; contact_id: number }) {
    return this.restService.postWithObservable(REST_SUBCONTRACT_INVITATIONS, payload);
  }
  acceptSubcontractInvite(inviteId: string, newProject?: Partial<ProjectDto>) {
    return this.restService.postWithObservable(
      `${REST_SUBCONTRACT_INVITATIONS}/${inviteId}/accept`,
      newProject ?? {},
    );
  }
  dismissSubcontractInvite(inviteId: string) {
    return this.restService.postWithObservable(
      `${REST_SUBCONTRACT_INVITATIONS}/${inviteId}/dismiss`,
      {},
    );
  }
  resendSubcontractInvite(inviteId: string) {
    return this.restService.postWithObservable(
      `${REST_SUBCONTRACT_INVITATIONS}/${inviteId}/resend`,
      {},
    );
  }
  cancelSentSubcontractInvite(inviteId: string) {
    return this.restService.postWithObservable(
      `${REST_SUBCONTRACT_INVITATIONS}/${inviteId}/cancel`,
      {},
    );
  }

  updateInviteGroup(inviteGroupId: number, data: { groupName: string }) {
    return this.restService.putWithObservable(`${REST_PRIME_INVITATIONS}/${inviteGroupId}`, data);
  }

  getUsersInvitedToProject(projectId: number): Observable<UserInvited[]> {
    return this.restService.getWithObservable(
      `${REST_BASE}invitations/projects/${projectId}/users`,
    );
  }
}
